<template>
  <v-card min-height="550">
    <v-toolbar flat width="100%">
      <v-toolbar-title class="text-capitalize title"
        >{{ $t("animal.geneticWorthInfo.economicIndex") }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        @click="showAddEditGeneticWorthEIDialog('edit')"
        outlined
        right
        height="50"
        width="241"
        color="primary"
      >
        {{ $t("editCurrentRecord") }}
      </v-btn>
      <v-btn
        @click="showAddEditGeneticWorthEIDialog('add')"
        right
        class="ml-5"
        height="50"
        width="241"
        color="primary"
        type="submit"
      >
        {{ $t("addNewRecord") }}
      </v-btn>
    </v-toolbar>
    <col-skeleton-loader v-if="loading" :clms="6"></col-skeleton-loader>
    <v-row width="100%" v-if="!loading">
      <v-col cols="6">
        <v-card class="pa-2 green2 elevation-0">
          <v-icon class="float-right">mdi-dots-horizontal</v-icon>
          <v-list class="green2">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-subtitle
                  >{{ $t("animal.geneticWorthInfo.economicIndex") }}
                  <v-tooltip top color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>{{
                      $t(
                        "animal.geneticWorthInfo.weightedAverageOfBreedingValues"
                      )
                    }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-title class="text-h6 mt-2">
                  {{ geneticWorthEI?.EI || "Not Specified" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card class="pa-2 green2 elevation-0">
          <v-icon class="float-right">mdi-dots-horizontal</v-icon>
          <v-list class="green2">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-subtitle
                  >{{ $t("animal.geneticWorthInfo.economicIndexAccuracy") }}
                  <v-tooltip top color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>{{
                      $t("animal.geneticWorthInfo.accuracyOfEconomicIndex")
                    }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-title class="text-h6 mt-2">
                  {{ geneticWorthEI?.EIAccuracy || "Not Specified" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card class="pa-2 green2 elevation-0">
          <v-icon class="float-right">mdi-dots-horizontal</v-icon>
          <v-list class="green2">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-subtitle
                  >{{ $t("animal.geneticWorthInfo.economicIndexPercentile") }}
                  <v-tooltip top color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>{{
                      $t("animal.geneticWorthInfo.percentileEconomicIndexFall")
                    }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-title class="text-h6 mt-2">
                  {{ geneticWorthEI?.EIAccuracyPercent || "Not Specified"
                  }}<span class="text-h6 text--secondary font-weight-light">{{
                    geneticWorthEI?.EIAccuracyPercentUnit || "%"
                  }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card class="pa-2 green2 elevation-0">
          <v-icon class="float-right">mdi-dots-horizontal</v-icon>
          <v-list class="green2">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-subtitle
                  >{{ $t("animal.geneticWorthInfo.economicIndexRank") }}
                  <v-tooltip top color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>{{
                      $t(
                        "animal.geneticWorthInfo.rankAnimalInRelationshipEconomicIndex"
                      )
                    }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-title class="text-h6 mt-2">
                  {{ geneticWorthEI?.EIRank || "Not Specified"
                  }}<span class="text-h6 text--secondary font-weight-light">
                    {{ geneticWorthEI?.EIRankUnit || "nd" }}
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card class="pa-2 green2 elevation-0">
          <v-icon class="float-right">mdi-dots-horizontal</v-icon>
          <v-list class="green2">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-subtitle
                  >{{ $t("animal.geneticWorthInfo.dailyProteinYieldWeight") }}
                  <v-tooltip top color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>{{
                      $t(
                        "animal.geneticWorthInfo.weightAssignedBreedingDailyProteinYield"
                      )
                    }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-title class="text-h6 mt-2">
                  {{ geneticWorthEI?.DPYWight || "Not Specified"
                  }}<span class="text-h6 text--secondary font-weight-light">{{
                    geneticWorthEI?.DPYWightUnit || $t("kG")
                  }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card class="pa-2 green2 elevation-0">
          <v-icon class="float-right">mdi-dots-horizontal</v-icon>
          <v-list class="green2">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-subtitle
                  >{{ $t("animal.geneticWorthInfo.dailyProteinYieldUsed") }}
                  <v-tooltip top color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>{{
                      $t("animal.geneticWorthInfo.breedingValueEconomicIndex")
                    }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-title class="text-h6 mt-2">
                  {{ geneticWorthEI?.DPYUsed == 1 ? $t("yes") : $t("no")
                  }}<span
                    class="text-h6 text--secondary font-weight-light"
                  ></span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <div class="mt-7">
      <span style="font-size: 20px">{{ $t('economicIndexHistory') }}</span>
      <v-data-table
        class="mt-7"
        :headers="headers"
        :items="showItems"
        :items-per-page="20"
        hide-default-footer
        show-select
        loading-text="Loading Data..."
      >
        <template #item.EI="{ item }">{{ item.EI }}</template>
        <template #item.EIAccuracy="{ item }">{{ item.EIAccuracy }} </template>
        <template #item.EIAccuracyPercent="{ item }"
          >{{ item.EIAccuracyPercent }}
        </template>
        <template #item.EIRank="{ item }">{{ item.EIRank }} </template>
        <template #item.DPYWight="{ item }">{{ item.DPYWight }} </template>
        <template #item.DPYUsed="{ item }">{{ item.DPYUsed }} </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-icon class="mr-2" @click="editHistory(item)">
              mdi-pencil
            </v-icon>
            <v-icon color="red" @click="deleteHistory(item)">
              mdi-delete
            </v-icon>
          </div>
        </template>
        <template v-slot:footer></template>
      </v-data-table>
    </div>
    <AddEditEconomicIndex
      @closeDialog="closeDialog"
      v-if="showAddEditDialog"
      :dialogI="showAddEditDialog"
      :geneticWorthEIItem="geneticWorthEIItem"
      @onUpdate="onUpdate"
    />
    <DeletePopup
      :dialogI="showDeleteDialog"
      v-if="showDeleteDialog"
      @delete="onDelete"
      @closeDialog="closeDeleteDialog"
    />
  </v-card>
</template>
<script>
import ColSkeletonLoader from "@/components/col-skeleton-loader.vue";
import gql from "graphql-tag";
import DeletePopup from "../DeletePopup.vue";
import AddEditEconomicIndex from "./AddEditEconomicIndex.vue";
import loading from "@/mixins/LoadingMixin";

export default {
  apollo: {
    allGeneticWorthEconomicIndex: {
      query: gql`
        query GeneticWorthEconomicIndex($animalId: Int!) {
          allGeneticWorthEconomicIndex(animalId: $animalId) {
            id
            EI
            EIAccuracy
            EIAccuracyPercent
            EIAccuracyPercentUnit
            EIRank
            EIRankUnit
            DPYWight
            DPYWightUnit
            DPYWightUom
            DPYUsed
          }
        }
      `,
      variables() {
        return {
          animalId: parseInt(this.$route.params.animal_number),
        };
      },
      result: function ({ data }) {
        if (data) {
          this.geneticWorthEI = data.allGeneticWorthEconomicIndex[0];
          this.loading = false;
        }
      },
      update: (data) => data.allGeneticWorthEconomicIndex,
      error: (err) =>
        this.$notify({
          title: "Error loading Genetic worth economic index",
          text: err,
          type: "error",
          position: "top right",
        }),
    },
  },
  computed: {
    showItems() {
      let showData = [...this.allGeneticWorthEconomicIndex];
      showData.shift();
      return showData;
    },
  },

  components: {
    ColSkeletonLoader,
    AddEditEconomicIndex,
    DeletePopup,
  },
  data() {
    return {
      showAddEditDialog: false,
      showDeleteDialog: false,
      geneticWorthEIItem: null,
      loading: true,
      geneticWorthEI: {},
      headers: [
        {
          text: this.$t("animal.geneticWorthInfo.economicIndex"),
          align: "start",
          sortable: true,
          value: "EI",
        },
        {
          text: this.$t("animal.geneticWorthInfo.economicIndexAccuracy") ,
          align: "start",
          sortable: true,
          value: "EIAccuracy",
        },
        {
          text: this.$t("animal.geneticWorthInfo.economicIndexPercentile"),
          align: "start",
          sortable: true,
          value: "EIAccuracyPercent",
        },
        {
          text: this.$t("animal.geneticWorthInfo.economicIndexRank"),
          align: "start",
          sortable: true,
          value: "EIRank",
        },
        {
          text: this.$t("animal.geneticWorthInfo.dailyProteinYieldWeight"),
          align: "start",
          sortable: true,
          value: "DPYWight",
        },
        {
          text: this.$t("animal.geneticWorthInfo.dailyProteinYieldUsed"),
          align: "start",
          sortable: true,
          value: "DPYUsed",
        },
        { text: "", value: "actions", sortable: false, class: "d-flex" },
      ],
    };
  },
  mixins: [loading],

  methods: {
    closeDialog() {
      this.showAddEditDialog = false;
      this.geneticWorthEIItem = null;
    },
    showAddEditGeneticWorthEIDialog(action) {
      if (action == "edit") {
        this.geneticWorthEIItem = this.geneticWorthEI;
      } else {
        this.geneticWorthEIItem = null;
      }
      this.showAddEditDialog = true;
    },
    deleteHistory(item) {
      this.geneticWorthEIItem = item;
      this.showDeleteDialog = true;
    },
    editHistory(item) {
      this.geneticWorthEIItem = item;
      this.showAddEditDialog = true;
    },

    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.geneticWorthEIItem = null;
    },
    async onDelete() {
      this.startLoading();
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation deleteGenericWorthEconomicIndexRecord($data: Int!) {
              deleteGenericWorthEconomicIndexRecord(id: $data)
            }
          `,
          variables: {
            data: parseInt(this.geneticWorthEIItem.id),
          },
        })
        .then(async (res) => {
          this.$notify({
            title: `History deleted successfully`,
            text: res.message,
            type: "success",
            position: "top right",
          });
          this.stopLoading();
          this.showDeleteDialog = false;
          this.deleteItem = null;
          this.onUpdate();
        });
    },
    async onUpdate() {
      await this.$apollo.queries.allGeneticWorthEconomicIndex.refetch();
    },
  },
};
</script>
